"use client";

import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import FashionProductCard from "@/components/fashion/common/FashionProductCard";
import { isCoolingDown, setCooldown } from "@/lib/popupCooldown";
import "./FashionBrowsePopup.scss";

const BROWSE_KEY      = "fashion_browse_popup";
const NEWSLETTER_KEY  = "fashion_newsletter_popup";
const STOREFRONT_ID   = process.env.NEXT_PUBLIC_STOREFRONT_ID || "23";
const DELAY_MS        = 30000;
const VISIBLE_COUNT   = 4;

export default function FashionBrowsePopup() {
  const [visible, setVisible]   = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [offset, setOffset]     = useState(0);
  const [currency, setCurrency] = useState("PKR");

  useEffect(() => {
    const fashionCurrency = Cookies.get("fashionCurrencyName");
    if (fashionCurrency) {
      setCurrency(fashionCurrency);
    }
  }, []);

  useEffect(() => {
    // Listen for currency changes
    const handleStorageChange = () => {
      const fashionCurrency = Cookies.get("fashionCurrencyName");
      if (fashionCurrency && fashionCurrency !== currency) {
        setCurrency(fashionCurrency);
        setLoading(true);
        setOffset(0);
        fetchProducts();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [currency]);

  const fetchProducts = useCallback(async () => {
    const isMobile = window.innerWidth < 768;
    const handle   = "fashion-page-women";
    const storefrontId = Cookies.get("fashionCurrencyId") || STOREFRONT_ID;
    const endpoint = isMobile
      ? `${process.env.NEXT_PUBLIC_BACKEND}/storefront/landing-page-mobile?storefront=${storefrontId}&handle=${handle}&type=category`
      : `${process.env.NEXT_PUBLIC_BACKEND}/storefront/landing-page-desktop?storefront=${storefrontId}&handle=${handle}&type=category`;

    try {
      const res  = await fetch(endpoint, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      let list = [];
      for (const section of data?.homepage ?? []) {
        if (section?.type === "products_carousel") {
          list = section?.select_type === "collections_section"
            ? section?.collections?.[0]?.products ?? []
            : section?.categories?.[0]?.products ?? [];
          if (list.length) break;
        }
      }
      setProducts(list);
    } catch (err) {
      console.error("[FashionBrowsePopup]", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const show = useCallback(() => {
    if (isCoolingDown(BROWSE_KEY)) return;
    setVisible(true);
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    // Already on cooldown — nothing to do
    if (isCoolingDown(BROWSE_KEY)) return;

    // Newsletter already dismissed → start 30s timer
    if (isCoolingDown(NEWSLETTER_KEY)) {
      const t = setTimeout(show, DELAY_MS);
      return () => clearTimeout(t);
    }

    // Wait for newsletter to be dismissed in this tab
    let t = null;
    const onStorage = (e) => {
      if (e.key === NEWSLETTER_KEY && e.newValue && !isCoolingDown(BROWSE_KEY)) {
        t = setTimeout(show, DELAY_MS);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (t) clearTimeout(t);
    };
  }, [show]);

  const handleClose = () => {
    setCooldown(BROWSE_KEY);
    setVisible(false);
  };

  const visibleProducts = products.slice(offset, offset + VISIBLE_COUNT);
  const canPrev = offset > 0;
  const canNext = offset + VISIBLE_COUNT < products.length;

  if (!visible) return null;

  return (
    <div className="fbp__overlay" onClick={handleClose}>
      <div className="fbp__modal" onClick={(e) => e.stopPropagation()}>
        <button className="fbp__close" onClick={handleClose} aria-label="Close">×</button>
        <h2 className="fbp__title">Can&apos;t find what you&apos;re looking for?</h2>
        <div className="fbp__divider" />

        {loading ? (
          <div className="fbp__loading">
            {[1, 2, 3, 4].map((i) => <div key={i} className="fbp__skeleton" />)}
          </div>
        ) : visibleProducts.length === 0 ? (
          <p className="fbp__empty">No products available.</p>
        ) : (
          <div className="fbp__carousel-wrapper">
            {canPrev && <button className="fbp__arrow fbp__arrow--prev" onClick={() => setOffset((o) => o - 1)} aria-label="Previous">‹</button>}
            <div className="fbp__grid">
              {visibleProducts.map((product) => (
                <div key={product.id || product.handle} onClick={handleClose}>
                  <FashionProductCard product={product} currency={currency} />
                </div>
              ))}
            </div>
            {canNext && <button className="fbp__arrow fbp__arrow--next" onClick={() => setOffset((o) => o + 1)} aria-label="Next">›</button>}
          </div>
        )}
      </div>
    </div>
  );
}
